/* ==========================================================================
   Noble Paw Co. — Theme interactions
   Vanilla JS, no dependencies. Progressive enhancement, a11y-aware.
   Todos los textos visibles vienen de window.NoblePaw.i18n (locales del tema).
   ========================================================================== */
(function () {
  "use strict";

  const root = document.documentElement;
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const tr = (key, fallback) => ((window.NoblePaw && window.NoblePaw.i18n) || {})[key] || fallback;
  const money = (cents) =>
    (window.NoblePaw && window.NoblePaw.formatMoney
      ? window.NoblePaw.formatMoney(cents)
      : "$" + (cents / 100).toFixed(2));

  /* ---------- Header: transparent -> solid on scroll ---------- */
  const header = $("[data-header]");
  if (header) {
    const isTransparent = header.classList.contains("header--transparent");
    const onScroll = () => {
      const scrolled = window.scrollY > 24;
      header.classList.toggle("is-scrolled", scrolled);
    };
    if (isTransparent) {
      window.addEventListener("scroll", onScroll, { passive: true });
      onScroll();
    }
  }

  /* ---------- Scroll reveal (IntersectionObserver) ---------- */
  const reveals = $$("[data-reveal]");
  if (reveals.length && "IntersectionObserver" in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    reveals.forEach((el, i) => {
      // gentle stagger for grouped items
      if (el.hasAttribute("data-reveal-stagger")) {
        el.style.setProperty("--reveal-delay", (i % 6) * 80 + "ms");
      }
      io.observe(el);
    });
  } else {
    reveals.forEach((el) => el.classList.add("is-visible"));
  }

  /* ---------- Overlay helpers ---------- */
  const overlay = $("[data-overlay]");
  const openLayer = (el) => {
    if (overlay) overlay.classList.add("is-open");
    el.classList.add("is-open");
    el.setAttribute("aria-hidden", "false");
    root.classList.add("no-scroll");
  };
  const closeLayers = () => {
    if (overlay) overlay.classList.remove("is-open");
    $$(".drawer.is-open, .mobile-nav.is-open, .modal.is-open").forEach((el) => {
      el.classList.remove("is-open");
      el.setAttribute("aria-hidden", "true");
    });
    $$(".search-overlay.is-open").forEach((el) => el.classList.remove("is-open"));
    root.classList.remove("no-scroll");
  };
  if (overlay) overlay.addEventListener("click", closeLayers);
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeLayers();
  });
  $$("[data-close]").forEach((b) => b.addEventListener("click", closeLayers));

  /* ---------- Cart drawer ---------- */
  const cartDrawer = $("[data-cart-drawer]");
  const openCart = () => {
    if (cartDrawer) {
      openLayer(cartDrawer);
      refreshCart();
    }
  };
  $$("[data-cart-open]").forEach((b) =>
    b.addEventListener("click", (e) => {
      e.preventDefault();
      openCart();
    })
  );

  /* ---------- Mobile nav & search ---------- */
  const mobileNav = $("[data-mobile-nav]");
  $$("[data-mobile-open]").forEach((b) =>
    b.addEventListener("click", () => mobileNav && openLayer(mobileNav))
  );
  const searchOverlay = $("[data-search-overlay]");
  $$("[data-search-open]").forEach((b) =>
    b.addEventListener("click", () => {
      if (!searchOverlay) return;
      searchOverlay.classList.add("is-open");
      root.classList.add("no-scroll");
      const input = $("input", searchOverlay);
      if (input) setTimeout(() => input.focus(), 120);
    })
  );

  /* ---------- Accordion (FAQ) — animated height ---------- */
  $$("[data-accordion] .accordion__item").forEach((item) => {
    const trigger = $(".accordion__trigger", item);
    const panel = $(".accordion__panel", item);
    if (!trigger || !panel) return;
    panel.style.height = item.hasAttribute("open") ? "auto" : "0px";
    trigger.addEventListener("click", (e) => {
      e.preventDefault();
      const isOpen = item.hasAttribute("open");
      if (isOpen) {
        panel.style.height = panel.scrollHeight + "px";
        requestAnimationFrame(() => (panel.style.height = "0px"));
        panel.addEventListener(
          "transitionend",
          () => item.removeAttribute("open"),
          { once: true }
        );
      } else {
        item.setAttribute("open", "");
        panel.style.height = panel.scrollHeight + "px";
        panel.addEventListener(
          "transitionend",
          () => {
            if (item.hasAttribute("open")) panel.style.height = "auto";
          },
          { once: true }
        );
      }
      trigger.setAttribute("aria-expanded", String(!isOpen));
    });
    panel.style.transition = "height var(--dur-base) var(--ease-out)";
    panel.style.overflow = "hidden";
  });

  /* ---------- Quantity steppers ---------- */
  document.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-qty]");
    if (!btn) return;
    const input = $("input", btn.closest(".qty"));
    if (!input) return;
    const step = btn.dataset.qty === "up" ? 1 : -1;
    const next = Math.max(1, (parseInt(input.value, 10) || 1) + step);
    input.value = next;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  });

  /* ==========================================================================
     CART — AJAX (add, change, render drawer)
     ========================================================================== */

  function setCartCount(count) {
    $$("[data-cart-count]").forEach((el) => {
      el.textContent = count;
      el.classList.toggle("is-active", count > 0);
      el.classList.add("is-bump");
      setTimeout(() => el.classList.remove("is-bump"), 350);
    });
  }

  async function fetchCart() {
    const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    return res.json();
  }

  async function addToCart(id, quantity, selling_plan) {
    const body = { items: [{ id, quantity }] };
    if (selling_plan) body.items[0].selling_plan = selling_plan;
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw await res.json();
    return res.json();
  }

  async function changeLine(key, quantity) {
    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity }),
    });
    return res.json();
  }

  function renderCart(cart) {
    setCartCount(cart.item_count);
    const body = $("[data-cart-body]");
    const foot = $("[data-cart-foot]");
    if (!body) return;

    if (cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-empty">
          <div class="cart-empty__icon">🐾</div>
          <h3 class="h3">${tr("emptyTitle", "Your cart is empty")}</h3>
          <p class="text-muted" style="margin:.5rem auto 1.5rem">${tr("emptySub", "")}</p>
          <a href="/collections/all" class="btn" data-close>${tr("explore", "Browse the store")}</a>
        </div>`;
      if (foot) foot.hidden = true;
      return;
    }
    if (foot) foot.hidden = false;

    const lines = cart.items
      .map(
        (it) => `
      <div class="cart-line" data-line-key="${it.key}">
        <img class="cart-line__img" src="${it.image ? it.image.replace(/(\.[^.]+)$/, "_160x$1") : ""}" alt="${it.product_title}" loading="lazy">
        <div>
          <div class="cart-line__title">${it.product_title}</div>
          ${it.variant_title && it.variant_title !== "Default Title" ? `<div class="cart-line__variant">${it.variant_title}</div>` : ""}
          <div class="cart-line__bottom">
            <div class="qty">
              <button type="button" data-qty="down" aria-label="${tr("decrease", "Decrease")}">−</button>
              <input type="text" value="${it.quantity}" data-line-qty inputmode="numeric" aria-label="${tr("quantity", "Quantity")}">
              <button type="button" data-qty="up" aria-label="${tr("increase", "Increase")}">+</button>
            </div>
            <button class="cart-line__remove" data-line-remove>${tr("remove", "Remove")}</button>
          </div>
        </div>
        <div class="cart-line__price">${money(it.final_line_price)}</div>
      </div>`
      )
      .join("");

    body.innerHTML = `
      <div data-cart-lines>${lines}</div>
      <div class="cart-upsell" data-cart-upsell hidden>
        <h3>${tr("upsellTitle", "Complete your order")}</h3>
        <div data-upsell-list></div>
      </div>`;
    loadUpsell(cart);

    const sub = $("[data-cart-subtotal]");
    if (sub) sub.textContent = money(cart.total_price);

    // line qty + remove handlers
    $$("[data-line-qty]", body).forEach((input) => {
      input.addEventListener("change", async () => {
        const key = input.closest("[data-line-key]").dataset.lineKey;
        const cart = await changeLine(key, parseInt(input.value, 10) || 1);
        renderCart(cart);
      });
    });
    $$("[data-line-remove]", body).forEach((btn) => {
      btn.addEventListener("click", async () => {
        const lineEl = btn.closest("[data-line-key]");
        lineEl.style.opacity = "0.4";
        const cart = await changeLine(lineEl.dataset.lineKey, 0);
        renderCart(cart);
      });
    });
  }

  async function loadUpsell(cart) {
    const box = $("[data-cart-upsell]");
    const list = $("[data-upsell-list]");
    if (!box || !list || !cart.items.length) return;
    const inCart = cart.items.map((i) => i.product_id);
    try {
      const pid = cart.items[0].product_id;
      const res = await fetch(
        "/recommendations/products.json?product_id=" + pid + "&limit=8&intent=related",
        { headers: { Accept: "application/json" } }
      );
      const data = await res.json();
      const recs = (data.products || [])
        .filter((p) => inCart.indexOf(p.id) === -1)
        .slice(0, 2);
      if (!recs.length) return;
      list.innerHTML = recs
        .map(function (p) {
          const v = (p.variants && p.variants[0]) || {};
          const img = p.featured_image || (p.images && p.images[0]) || "";
          return (
            '<div class="upsell-item">' +
            '<img src="' + img + '" alt="' + p.title + '" loading="lazy">' +
            '<div><div class="upsell-item__name">' + p.title + "</div>" +
            '<div class="text-muted" style="font-size:var(--fs-xs)">' + money(p.price) + "</div></div>" +
            '<button type="button" class="upsell-item__add" data-quick-add="' + v.id + '">' + tr("addShort", "Add") + "</button>" +
            "</div>"
          );
        })
        .join("");
      box.hidden = false;
    } catch (e) {
      /* noop */
    }
  }

  async function refreshCart() {
    try {
      renderCart(await fetchCart());
    } catch (e) {
      /* noop */
    }
  }

  // Add-to-cart forms (PDP, quick add)
  document.addEventListener("submit", async (e) => {
    const form = e.target.closest("[data-add-to-cart]");
    if (!form) return;
    e.preventDefault();
    const btn = $('[type="submit"]', form);
    const idInput = $('[name="id"]', form);
    const qtyInput = $('[name="quantity"]', form);
    if (!idInput || !idInput.value) return;
    const label = btn ? btn.innerHTML : "";
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = '<span class="spinner"></span>';
    }
    try {
      await addToCart(
        idInput.value,
        qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1,
        form.dataset.sellingPlan
      );
      await refreshCart();
      openCart();
      if (btn) btn.innerHTML = tr("added", "✓ Added");
      setTimeout(() => {
        if (btn) {
          btn.innerHTML = label;
          btn.disabled = false;
        }
      }, 1200);
    } catch (err) {
      if (btn) {
        btn.innerHTML = (err && err.description) || tr("unavailable", "Unavailable");
        setTimeout(() => {
          btn.innerHTML = label;
          btn.disabled = false;
        }, 1600);
      }
    }
  });

  // Quick add buttons (collection cards / upsell)
  document.addEventListener("click", async (e) => {
    const btn = e.target.closest("[data-quick-add]");
    if (!btn) return;
    e.preventDefault();
    const id = btn.dataset.quickAdd;
    if (!id || id === "undefined") return;
    const original = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '<span class="spinner"></span>';
    try {
      await addToCart(id, 1);
      await refreshCart();
      openCart();
    } catch (err) {
      /* noop */
    } finally {
      btn.innerHTML = original;
      btn.disabled = false;
    }
  });

  /* ==========================================================================
     PRODUCT PAGE — gallery + variant selection + sticky ATC
     ========================================================================== */
  const pdp = $("[data-pdp]");
  if (pdp) {
    // Gallery thumbs
    const mainImg = $("[data-pdp-main] img", pdp);
    $$("[data-pdp-thumb]", pdp).forEach((thumb) => {
      thumb.addEventListener("click", () => {
        const full = thumb.dataset.pdpThumb;
        if (mainImg && full) mainImg.src = full;
        $$("[data-pdp-thumb]", pdp).forEach((t) => t.classList.remove("is-active"));
        thumb.classList.add("is-active");
      });
    });

    // Variant selection -> update price, id, availability
    const variantData = (() => {
      try {
        return JSON.parse($("[data-variant-json]", pdp).textContent);
      } catch (e) {
        return [];
      }
    })();
    const form = $("[data-add-to-cart]", pdp);
    const idField = form ? $('[name="id"]', form) : null;
    const priceEl = $("[data-pdp-price]", pdp);
    const submitBtn = form ? $('[type="submit"]', form) : null;

    function currentOptions() {
      return $$("[data-option-index]", pdp).map((group) => {
        const checked = $('input:checked', group);
        return checked ? checked.value : null;
      });
    }
    const stickyPriceEl = $(".sticky-atc__price");
    const stickyBtn = $("[data-sticky-atc] [data-quick-add]");

    function setBuyState(label, enabled) {
      if (!submitBtn) return;
      submitBtn.disabled = !enabled;
      const span = submitBtn.querySelector("[data-btn-label]");
      if (span) span.textContent = label;
      else submitBtn.textContent = label;
      if (stickyBtn) {
        stickyBtn.disabled = !enabled;
        stickyBtn.textContent = label;
      }
    }

    function updateVariant() {
      const opts = currentOptions();
      const match = variantData.find((v) =>
        v.options.every((o, i) => o === opts[i])
      );
      if (!match) {
        // Combinación que no existe: nunca dejar activa la variante anterior.
        if (idField) idField.value = "";
        setBuyState(tr("unavailable", "Unavailable"), false);
        return;
      }
      if (idField) idField.value = match.id;
      if (priceEl) priceEl.textContent = money(match.price);
      if (stickyPriceEl) stickyPriceEl.textContent = money(match.price);
      if (stickyBtn) stickyBtn.setAttribute("data-quick-add", match.id);
      setBuyState(
        match.available ? tr("add", "Add to cart") : tr("soldOut", "Sold out"),
        match.available
      );
    }
    $$("[data-option-index] input", pdp).forEach((input) =>
      input.addEventListener("change", updateVariant)
    );

    // Pack selector: keep a single active card
    $$(".pack-card input", pdp).forEach((input) =>
      input.addEventListener("change", function () {
        $$(".pack-card", pdp).forEach((c) => c.classList.remove("is-active"));
        var card = input.closest(".pack-card");
        if (card) card.classList.add("is-active");
      })
    );

    // Sticky ATC bar visibility
    const stickyBar = $("[data-sticky-atc]");
    const buyBox = $("[data-pdp-buy]", pdp);
    if (stickyBar && buyBox && "IntersectionObserver" in window) {
      const obs = new IntersectionObserver(
        ([entry]) => stickyBar.classList.toggle("is-visible", !entry.isIntersecting),
        { rootMargin: "-80px 0px 0px 0px" }
      );
      obs.observe(buyBox);
    }
  }

  /* ==========================================================================
     RECENTLY VIEWED (localStorage)
     ========================================================================== */
  try {
    const handle = root.dataset.productHandle;
    const KEY = "dcm_recently_viewed";
    let viewed = JSON.parse(localStorage.getItem(KEY) || "[]");
    if (handle) {
      viewed = [handle, ...viewed.filter((h) => h !== handle)].slice(0, 8);
      localStorage.setItem(KEY, JSON.stringify(viewed));
    }
  } catch (e) {
    /* noop */
  }

  /* ---------- Newsletter & contact: graceful inline feedback ---------- */
  $$("[data-async-form]").forEach((form) => {
    form.addEventListener("submit", () => {
      const btn = $('[type="submit"]', form);
      if (btn) btn.innerHTML = '<span class="spinner"></span>';
    });
  });

  /* ---------- Elegant image load fade (perceived speed) ---------- */
  function fadeInImages(scope) {
    $$(
      ".product-card__media img.is-primary, .hero__product, .pdp__main-img img, .collection-card img, .article-card__media img",
      scope || document
    ).forEach(function (img) {
      if (img.dataset.faded) return;
      img.dataset.faded = "1";
      if (img.complete && img.naturalWidth) return; // already painted
      img.classList.add("js-imgfade");
      const reveal = function () { img.classList.add("is-loaded"); };
      img.addEventListener("load", reveal, { once: true });
      img.addEventListener("error", reveal, { once: true });
    });
  }
  fadeInImages();

  // Expose for sections
  window.NoblePaw = Object.assign(window.NoblePaw || {}, { refreshCart, openCart, addToCart, fadeInImages });
})();
